import TheRWSService from "@rws-framework/client/src/services/_service";

import { type EmbedInfo, type UploadedFile } from "./types";
import { HTTPClient } from "src/services/httpClient";
import { Cache } from "src/helpers/cache";

/**
 * Thin wrapper around FileUploaderEmbedService,
 * to be passed around components instead of passing multiple callbacks.
 * (simplifies mocking in storybook too)
 */
export interface EmbedApi {
  detect(url: string, fileType: UploadedFile["file"]["type"]): EmbedInfo | null;

  fetchMetadata(
    url: string,
    embed: EmbedInfo,
  ): Promise<{ title: string; description: string }>;

  canBeEmbedded(
    url: string,
    embed: EmbedInfo,
  ): Promise<{ valid: boolean; message?: string }>;
}

/**
 * Class for detecting which external URLs can be embedded (e.g. YouTube, Vimeo),
 * and for fetching their metadata.
 */
class FileUploaderEmbedService extends TheRWSService {
  static _IN_CLIENT: boolean = true;

  public static detectors = registerDetectors();
  private cache = new Cache();
  private http = new HTTPClient({});

  static detect(
    url: string,
    fileType: UploadedFile["file"]["type"],
  ): EmbedInfo | null {
    const detectedService = FileUploaderEmbedService.detectors.find(
      (d) => d.template === fileType && url.match(d.url),
    );

    return detectedService
      ? {
          embedSrc: !detectedService.embeddable
            ? detectedService.getEmbed(url)
            : url,
          serviceTag: detectedService.serviceTag,
        }
      : null;
  }

  fetchMetadata(
    url: string,
    embed: EmbedInfo,
  ): Promise<{ title: string; description: string }> {
    const detectedService = FileUploaderEmbedService.detectors.find((d) =>
      url.match(d.url),
    );
    const fallback = Promise.resolve({ title: "", description: "" });

    if (!detectedService) return fallback;

    switch (embed.serviceTag) {
      case "youtube": {
        const id = detectedService.getKey(url);
        const key = window.edrnaConfig.googlePublicKey;
        const req = `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${id}&key=${key}`;

        return fetch(req).then((response) => {
          if (response.status !== 200) {
            return fallback;
          }

          return response.json().then((res) => ({
            title: res.items[0].snippet.title,
            description: res.items[0].snippet.description,
          }));
        });
      }
      case "vimeo": {
        let req = url;
        if (req.substr(0, 4) !== "http") req = "https://" + req;
        if (req.substr(0, 6) !== "https:") req = req.replace("http", "https");
        if (req.substr(0, 33) !== "https://vimeo.com/api/oembed.json") {
          req = "https://vimeo.com/api/oembed.json?url=" + req;
        }
        req = req.replace("player.", "").replace("video/", "");

        return fetch(req, {}).then((res) => {
          if (res.status !== 200) {
            return fallback;
          }
          return res.json().then((res) => ({
            title: res.title,
            description: res.description,
          }));
        });
      }
      case "slideshare": {
        // slidshare seems to not support CORS...
        // https://www.slideshare.net/api/oembed/2?format=json&url=${url}
        return fallback;
      }
      // https://developers.soundcloud.com/docs/oembed#introduction
      case "soundcloud": {
        let req = url;
        if (req.substr(0, 4) !== "http") req = "https://" + req;
        if (req.substr(0, 5) !== "https") req = req.replace("http", "https");
        if (req.substr(0, 33) !== "https://soundcloud.com/oembed") {
          req = "https://soundcloud.com/oembed?url=" + req;
        }

        return fetch(req, {}).then((res) => {
          if (res.status !== 200) {
            return fallback;
          }
          return res.json().then((res) => ({
            title: res.title,
            description: res.description,
          }));
        });
      }
      case "brightcove": {
        return this.brightcoveKeys().then((playerKeys) => {
          const { playerId, playerEmbedId, accountId, videoId } =
            parseBrightcoveUrl(url);
          const key = playerKeys.find(
            (k) => k.playerId === playerId || k.playerId === playerEmbedId,
          );

          if (!key) return fallback;

          const metadataUrl = `https://edge.api.brightcove.com/playback/v1/accounts/${accountId}/videos/${videoId}`;

          return fetch(metadataUrl, {
            headers: {
              accept: `application/json;pk=${key.policyKey}`,
            },
          })
            .then((res) =>
              res.ok
                ? res.json()
                : Promise.reject(
                    new Error("Brightcove API error" + res.statusText),
                  ),
            )
            .then((video) => {
              const { name: title = "", description = "" } = video;

              return { title, description };
            });
        });
      }
      default: {
        return fallback;
      }
    }
  }

  canBeEmbedded(
    url: string,
    embed: EmbedInfo,
  ): Promise<{ valid: boolean; message?: string }> {
    switch (embed.serviceTag) {
      case "brightcove": {
        return this.brightcoveKeys()
          .then((playerKeys) => {
            const { playerId, playerEmbedId } = parseBrightcoveUrl(url);

            const policyKey = playerKeys.find(
              (k) => k.playerId === playerId || k.playerId === playerEmbedId,
            );

            if (!policyKey)
              return {
                valid: false,
                message: "No Policy Key associated with provided Player URL.",
              };

            return { valid: true };
          })
          .catch((err) => ({
            valid: false,
            message: "Failed to load Brightcove Keys.",
          }));
      }

      default: {
        return Promise.resolve({ valid: true });
      }
    }
  }

  asApi(): EmbedApi {
    return {
      detect: FileUploaderEmbedService.detect,
      fetchMetadata: this.fetchMetadata.bind(this),
      canBeEmbedded: this.canBeEmbedded.bind(this),
    };
  }

  resellerSettings = this.cache.cached<
    FileUploaderEmbedService["resellerSettingsUncached"]
  >(
    () => "theme",
    () => this.resellerSettingsUncached(),
  );
  private resellerSettingsUncached(): Promise<{
    settings: Record<string, unknown>;
  }> {
    return this.http.get("/theme");
  }

  async brightcoveKeys(): Promise<
    Array<{ playerId: string; policyKey: string }>
  > {
    const { settings } = await this.resellerSettings();

    return settings.brightcove_keys && Array.isArray(settings.brightcove_keys)
      ? settings.brightcove_keys.filter((k) => k.playerId && k.policyKey)
      : [];
  }
}

export default FileUploaderEmbedService.getSingleton();
export { FileUploaderEmbedService as FileUploaderEmbedServiceInstance };

/** Based on frontend/app/core/detectors.js */
export function registerDetectors() {
  const data: Array<{
    template: UploadedFile["file"]["type"];
    url: string;
    serviceTag: string;
    embeddable?: boolean;
    getOriginal?(url: string): string;
    getEmbed?(url: string, override?: unknown): string;
    getPlayerVars?(url: string): { start?: string; end?: string };
    getKey?(url: string): string;
  }> = [];

  /* VIDEO */

  data.push({
    template: "video",
    url: "http(s|)://www.youtu(.be/|be.com/watch\\?v=)([\\w-]{11})(\\&start=(\\d+))?(\\&end=(\\d+))?",
    serviceTag: "youtube",
    embeddable: false,
    getEmbed: function (url) {
      return (
        "http" +
        url.match(this.url)[1] +
        "://www.youtube.com/embed/" +
        this.getKey(url)
      );
    },
    getPlayerVars: function (url) {
      var d = url.match(this.url);
      if (!d[5]) return {};
      return { start: d[5], end: d[7] };
    },
    getKey: function (url) {
      var d = url.match(this.url);
      return d[3];
    },
  });

  data.push({
    template: "video",
    url: "(www.|)youtu(.be/|be.com/watch\\?v=)([\\w-]{11})(\\&start=(\\d+))?(\\&end=(\\d+))?",
    serviceTag: "youtube",
    embeddable: false,
    getEmbed: function (url) {
      return "http://www.youtube.com/embed/" + this.getKey(url);
    },
    getPlayerVars: function (url) {
      var d = url.match(this.url);
      if (!d[5]) return {};
      return { start: d[5], end: d[7] };
    },
    getKey: function (url) {
      var d = url.match(this.url);
      return d[3];
    },
  });

  data.push({
    template: "video",
    url: "http(s|)://(www.)?youtube.com/embed/([\\w-]{11})(.*)",
    serviceTag: "youtube",
    embeddable: true,
    getOriginal: function (url) {
      var d = url.match(this.url);
      if (!d[4]) return "https://www.youtube.com/watch?v=" + d[3];
      return "https://www.youtube.com/embed/" + d[3] + d[4];
    },
    getPlayerVars: function (url) {
      var d = url.match(this.url);
      if (!d[4]) return {};
      var s = d[4].match("start=(\\d+)");
      var e = d[4].match("end=(\\d+)");
      var r = {} as { start?: string; end?: string };
      if (s && s[1]) r.start = s[1];
      if (e && e[1]) r.end = e[1];
      return r;
    },
    getKey: function (url) {
      var d = url.match(this.url);
      return d[3];
    },
  });

  data.push({
    template: "video",
    url: "(www.|)youtube.com/embed/([\\w-]{11})(\\?start=(\\d+))?(\\&end=(\\d+))?",
    serviceTag: "youtube",
    embeddable: true,
    getOriginal: function (url) {
      var d = url.match(this.url);
      if (!d[3]) return "http://www.youtube.com/watch?v=" + d[2];
      return (
        "https://www.youtube.com/watch?v=" + d[2] + "&start=" + d[4] + d[5]
      );
    },
    getPlayerVars: function (url) {
      var d = url.match(this.url);
      if (!d[4]) return {};
      return { start: d[4], end: d[6] };
    },
    getKey: function (url) {
      var d = url.match(this.url);
      return d[2];
    },
  });

  // https://vimeo.com/347119375
  data.push({
    // this is above the next regex because it needs to catch first if possible
    template: "video",
    url: "(http(s)?://)?player.vimeo.com/video/([0-9]+)",
    serviceTag: "vimeo",
    embeddable: true,
    getOriginal: function (url) {
      return "https://www.vimeo.com/" + url.match(this.url)[3];
    },
  });

  data.push({
    template: "video",
    url: "(http(s)?://)?(www.)?vimeo.com/(channels/([a-z0-9]+)/)?([0-9]+)",
    serviceTag: "vimeo",
    embeddable: false,
    getEmbed: function (url) {
      return "https://player.vimeo.com/video/" + url.match(this.url)[6];
    },
  });

  data.push({
    template: "video",
    url: "http(s|)://www.vevo.com/watch/([A-Z0-9]{12})",
    serviceTag: "vevo",
    embeddable: false,
    getEmbed: function (url) {
      return (
        "http://cache.vevo.com/assets/html/embed.html?video=" +
        url.match(this.url)[2]
      );
    },
  });

  data.push({
    template: "video",
    url: "http://cache.vevo.com/assets/html/embed.html\\?video=([A-Z0-9]{12})",
    serviceTag: "vevo",
    embeddable: true,
    getOriginal: function (url) {
      return "http://vevo.com/watch/" + url.match(this.url)[1];
    },
  });

  data.push({
    template: "video",
    url: "http(s|):\\/\\/players\\.brightcove\\.net([^?]*?)\\?videoId=(\\w+)",
    serviceTag: "brightcove",
    embeddable: true,
    getKey: function (url) {
      return url.match(this.url)[3];
    },
  });

  // https://www.slideshare.net/slideshow/embed_code/key/F89jlIlQM4HT1q
  data.push({
    template: "presentation",
    url: "(https?://)?www.slideshare.net/slideshow/embed_code/key/([a-zA-Z0-9]+)",
    serviceTag: "slideshare",
    getEmbed: function (url) {
      const key = url.match(this.url)[2];
      return "https://www.slideshare.net/slideshow/embed_code/key/" + key;
    },
  });

  // TODO: what about https://www.slideshare.net/slideshow/wtf-why-the-future-is-up-to-us-pptx-version/70964752
  data.push({
    template: "presentation",
    url: "(https?://)?www.slideshare.net/([a-zA-Z0-9]+)/([a-zA-Z0-9-]+)",
    serviceTag: "slideshare",
    getEmbed: function (key) {
      return "https://www.slideshare.net/slideshow/embed_code/key/" + key;
    },
  });

  data.push({
    template: "document",
    url: "^(https?://)?drive.google.com/open\\?id=(.+?)(&|#|$)",
    serviceTag: "google-docs",
    embeddable: false,
    getEmbed: function (key) {
      return (
        "https://docs.google.com/file/d/" + key.match(this.url)[2] + "/preview"
      );
    },
  });

  data.push({
    template: "presentation",
    url: "(https?://)?docs.google.com/presentation/d/e/(.*?)/.*",
    serviceTag: "google-docs",
    embeddable: false,
    getEmbed: function (key) {
      return (
        "https://docs.google.com/presentation/d/e/" +
        key.match(this.url)[2] +
        "/embed"
      );
    },
  });

  data.push({
    template: "document",
    url: "(https?://)?docs.google.com/(?!forms).*?/d/(.*?)/.*",
    serviceTag: "google-docs",
    embeddable: false,
    getEmbed: function (key) {
      return (
        "https://docs.google.com/file/d/" + key.match(this.url)[2] + "/preview"
      );
    },
  });

  data.push({
    template: "audio",
    url: "(https://w.soundcloud.com/player(.+?)visual=true)",
    serviceTag: "soundcloud",
    embeddable: false,
    getEmbed: function (key) {
      return key.match(this.url)[1];
    },
  });

  data.push({
    template: "audio",
    url: "^https://soundcloud.com/",
    serviceTag: "soundcloud",
    embeddable: false,
    getEmbed: function (key) {
      return key;
    },
  });

  return data;
}

export function parseBrightcoveUrl(url: string) {
  // https://players.brightcove.net/<ACCOUNT_ID>/<PLAYER_ID>_<EMBED_ID>/index.html?videoId=<VIDEO_ID>
  const videoUrl = new URL(url);
  const [_, accountId, playerEmbedId] = videoUrl.pathname.split("/");
  const [playerId, embedId] = playerEmbedId.split("_");
  const videoId = videoUrl.searchParams.get("videoId");

  return { videoUrl, accountId, playerId, embedId, playerEmbedId, videoId };
}
